import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import Nav from './nav'; // same nav component as your dashboards

// ===== Visual tokens (kept consistent with your dashboard) =====
const brandColor = '#005CC5';
const TYPE_META = {
  external: { label: 'External', icon: PublicIcon,    color: '#10b981' },
  web:      { label: 'Web',      icon: LanguageIcon,  color: '#3b82f6' },
  api:      { label: 'API',      icon: LanguageIcon,  color: '#06b6d4' },
  vishing:  { label: 'Vishing',  icon: PhoneIcon,     color: '#f59e0b' },
  darkweb:  { label: 'Dark Web', icon: LockIcon,      color: '#8b5cf6' },
};
const TYPE_KEYS = ['external', 'web', 'api', 'vishing', 'darkweb'];

const withOpacity = (hex, opa = 0.14) => alpha(hex, opa);

// ===== Utilities =====
const startOfMonthGrid = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  // Monday as start of week (to match your dash vibe)
  const day = (d.getDay() + 6) % 7; // 0..6 (Mon..Sun)
  d.setDate(d.getDate() - day);
  d.setHours(0,0,0,0);
  return d;
};
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Simple repeat helpers for the mock schedule
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addWeeks = (d, n) => addDays(d, n * 7);

// Build a month grid (6 weeks x 7 days = 42 cells)
const buildMonthGrid = (current) => {
  const first = startOfMonthGrid(current);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dt = addDays(first, i);
    cells.push(dt);
  }
  return cells;
};

// ===== Mock scheduled events (for visual only) =====
// Each event: { date: Date, type: 'external'|'web'|'api'|'vishing'|'darkweb', name, cadence }
const buildMockEvents = (visibleMonthDate) => {
  // Use the visible month to keep examples relevant
  const month = visibleMonthDate.getMonth();
  const year = visibleMonthDate.getFullYear();

  // Anchors
  const base = new Date(year, month, 2);
  const mondayAnchor = new Date(year, month, 5); // a Monday-ish

  const samples = [];

  // Weekly External every Monday morning
  for (let w = 0; w < 6; w++) {
    const d = addWeeks(mondayAnchor, w);
    samples.push({
      date: d,
      type: 'external',
      name: 'External Surface Sweep',
      cadence: 'Weekly',
    });
  }

  // Bi-weekly Web on Wednesdays
  const wed = new Date(year, month, 1);
  while (wed.getDay() !== 3) wed.setDate(wed.getDate() + 1); // find first Wednesday
  for (let w = 0; w < 3; w++) {
    samples.push({
      date: addWeeks(wed, w * 2),
      type: 'web',
      name: 'Web App Deep Crawl',
      cadence: 'Bi-weekly',
    });
  }

  // Monthly API on the 10th
  const apiDay = new Date(year, month, 10);
  samples.push({ date: apiDay, type: 'api', name: 'API Contract & Auth Suite', cadence: 'Monthly' });

  // Quarterly Dark Web on the 15th (shown even if not quarter boundary—purely illustrative)
  const darkDay = new Date(year, month, 15);
  samples.push({ date: darkDay, type: 'darkweb', name: 'Dark Web Leak Sweep', cadence: 'Quarterly' });

  // One-time Vishing on the 22nd
  const visDay = new Date(year, month, 22);
  samples.push({ date: visDay, type: 'vishing', name: 'Finance Dept Vishing Drill', cadence: 'One-time' });

  // Rolling “Continuous” External Agent: tiny marker across most weekdays
  for (let i = 1; i <= 28; i++) {
    const d = new Date(year, month, i);
    if ([0,6].includes(d.getDay())) continue; // skip weekends
    samples.push({
      date: d,
      type: 'external',
      name: 'Continuous Agent',
      cadence: 'Continuous',
      tiny: true,
    });
  }

  return samples;
};

// Group events by day for quick rendering
const useEventsByDay = (currentMonth) => {
  const events = useMemo(() => buildMockEvents(currentMonth), [currentMonth]);
  return useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = e.date.toDateString();
      map[key] = map[key] || [];
      map[key].push(e);
    });
    return map;
  }, [events]);
};

// ===== Event pill =====
const EventPill = ({ ev }) => {
  const meta = TYPE_META[ev.type] || TYPE_META.external;
  return (
    <Tooltip title={`${ev.name} • ${meta.label} • ${ev.cadence}`} arrow>
      <Chip
        size="small"
        label={ev.tiny ? 'Agent' : ev.name}
        sx={{
          height: 22,
          fontSize: 11,
          borderRadius: 1.5,
          bgcolor: withOpacity(meta.color, ev.tiny ? 0.10 : 0.18),
          color: meta.color,
          border: `1px solid ${alpha(meta.color, 0.45)}`,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};

// ===== Calendar cell =====
const CalendarCell = ({ date, inThisMonth, today, events = [] }) => {
  const isWeekend = [0,6].includes(date.getDay());
  return (
    <Box
      sx={{
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: (t) => alpha(t.palette.divider, 0.7),
        p: 1,
        minHeight: 110,
        bgcolor: inThisMonth ? 'background.paper' : (t) => alpha(t.palette.grey[100], 0.5),
        opacity: inThisMonth ? 1 : 0.6,
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: today ? brandColor : (isWeekend ? 'text.secondary' : 'text.primary'),
            }}
          >
            {date.getDate()}
          </Typography>
          {today && (
            <Chip
              size="small"
              icon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
              label="Today"
              sx={{
                height: 22,
                bgcolor: alpha(brandColor, 0.1),
                border: `1px solid ${alpha(brandColor, 0.35)}`,
                color: brandColor,
                '& .MuiChip-label': { px: 0.75, fontWeight: 700, fontSize: 11 },
              }}
            />
          )}
        </Stack>

        <Stack spacing={0.5}>
          {events.slice(0, 3).map((ev, i) => (<EventPill key={i} ev={ev} />))}
          {events.length > 3 && (
            <Tooltip title={events.slice(3).map(e => e.name).join(' • ')} arrow>
              <Chip
                size="small"
                label={`+${events.length - 3} more`}
                sx={{
                  height: 22,
                  fontSize: 11,
                  borderRadius: 1.5,
                  bgcolor: alpha('#111', 0.06),
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// ===== Main Page =====
const OrganizationScheduling = () => {
  const navigate = useNavigate();
  const { id: organizationId } = useParams();

  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    d.setHours(0,0,0,0);
    return d;
  });

  const [cadence, setCadence] = useState('one-time'); // purely visual for now
  const [tz, setTz] = useState('America/New_York');

  const gridDates = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const eventsByDay = useEventsByDay(viewMonth);

  const today = new Date(); today.setHours(0,0,0,0);
  const inSameMonth = (d) => d.getMonth() === viewMonth.getMonth() && d.getFullYear() === viewMonth.getFullYear();

  const nextMonth = () => {
    const d = new Date(viewMonth);
    d.setMonth(d.getMonth() + 1);
    setViewMonth(d);
  };
  const prevMonth = () => {
    const d = new Date(viewMonth);
    d.setMonth(d.getMonth() - 1);
    setViewMonth(d);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: (t) => alpha(t.palette.grey[100], 0.8) }}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Nav organizationId={organizationId} page="scheduling" />

        {/* Header */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: alpha(brandColor, 0.12), color: brandColor }}>
                <EventAvailableIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Scheduling — Coming Soon
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Automate one-time and recurring pentests (weekly, bi-weekly, monthly, quarterly, yearly) — or run continuous AI pentesting 24/7 to catch regressions instantly.
                </Typography>
              </Box>
            </Stack>

           
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* “Coming soon” controls (disabled) */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ opacity: 0.7 }}>Cadence</Typography>
              <Tooltip title="Scheduling configuration is coming soon" arrow>
                <span>
                  <ToggleButtonGroup
                    exclusive
                    value={cadence}
                    onChange={(_, v) => v && setCadence(v)}
                    size="small"
                    disabled
                    sx={{
                      borderRadius: 2,
                      '& .MuiToggleButton-root': {
                        fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, px: 1.2, borderColor: 'transparent'
                      },
                      '& .Mui-selected': {
                        bgcolor: alpha(brandColor, 0.08),
                        border: '1.5px solid ' + alpha(brandColor, 0.35),
                        color: brandColor,
                      },
                    }}
                  >
                    <ToggleButton value="one-time">One-time</ToggleButton>
                    <ToggleButton value="weekly">Weekly</ToggleButton>
                    <ToggleButton value="bi-weekly">Bi-weekly</ToggleButton>
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="quarterly">Quarterly</ToggleButton>
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                  </ToggleButtonGroup>
                </span>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ opacity: 0.7 }}>Time Zone</Typography>
              <Tooltip title="Time zone selection will be enabled on launch" arrow>
                <span>
                  <Select size="small" value={tz} disabled sx={{ minWidth: 240 }}>
                    <MenuItem value="America/New_York">America/New_York (EST/EDT)</MenuItem>
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/Los_Angeles">America/Los_Angeles</MenuItem>
                  </Select>
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: alpha(brandColor, 0.06),
              border: `1px dashed ${alpha(brandColor, 0.35)}`,
              color: brandColor,
              fontWeight: 700,
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
            Scheduling & continuous red-teaming are in active development.
          </Box>
        </Card>

        {/* Calendar Card */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={prevMonth}><ArrowBackIosNewIcon /></IconButton>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {viewMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={nextMonth}><ArrowForwardIosIcon /></IconButton>
            </Stack>

            {/* Legend */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {TYPE_KEYS.map((k) => {
                const meta = TYPE_META[k];
                return (
                  <Chip
                    key={k}
                    size="small"
                    icon={<meta.icon sx={{ fontSize: 16, color: meta.color }} />}
                    label={meta.label}
                    sx={{
                      bgcolor: alpha(meta.color, 0.10),
                      color: meta.color,
                      border: `1px solid ${alpha(meta.color, 0.35)}`,
                      '& .MuiChip-label': { fontWeight: 700 },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Weekday header */}
          <Grid container columns={7} sx={{ borderTop: '1px solid', borderLeft: '1px solid', borderColor: (t) => alpha(t.palette.divider, 0.7) }}>
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((wd) => (
              <Grid item xs={1} key={wd}>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: (t) => alpha(t.palette.grey[100], 0.6),
                    borderRight: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: (t) => alpha(t.palette.divider, 0.7),
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, opacity: 0.8 }}>
                    {wd}
                  </Typography>
                </Box>
              </Grid>
            ))}

            {/* 6x7 grid */}
            {gridDates.map((d, i) => {
              const key = d.toDateString();
              const inMonth = inSameMonth(d);
              const todays = sameDay(d, today);
              return (
                <Grid item xs={1} key={key + i}>
                  <CalendarCell
                    date={d}
                    inThisMonth={inMonth}
                    today={todays}
                    events={eventsByDay[key] || []}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Card>

        {/* Value props / explainer */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: alpha(brandColor, 0.12), color: brandColor }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Set & Forget</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Define once, then let agents run on your schedule: weekly, bi-weekly, monthly, quarterly, or yearly. Perfect for compliance windows and steady coverage.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: alpha('#16a34a', 0.12), color: '#16a34a' }}>
                    <PublicIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Continuous Pentesting</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  24/7 AI red-teaming means new code or infra changes get tested as they land — catching fresh vulnerabilities immediately instead of at next quarter’s audit.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: alpha('#8b5cf6', 0.12), color: '#8b5cf6' }}>
                    <LanguageIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Multi-Agent Coverage</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Schedule specialized agents per surface: External, Web, API, Vishing (social engineering), and Dark Web. Scale tests without human bottlenecks.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

       
      </Box>
    </Box>
  );
};

export default OrganizationScheduling;
